import React, { FC } from "react";
import { Button } from "antd";
import { ExternalMatter, BaseAccruedFee } from "../../data-types";
import { HistoricInfo } from "../historic-info";
import { useSettings } from "~/hooks/settings";
import styles from "./create-proposal.css";
import { VPDLabel } from "~/context/taxonomy-context";

const {
  "matter-metadata": matterMetadataClassName,
  "matter-metadata-item": matterMetadataItemClassName,
  "matter-metadata-label": matterMetadataLabelClassName,
  "matter-metadata-value": matterMetadataValueClassName,
  "matter-metadata-separator": matterMetadataSeparatorClassName,
  "matter-creation-info": matterCreationInfoClassName,
} = styles;

interface RelevantExternalMatterPageProps {
  externalMatter: ExternalMatter;
  accruedFees?: BaseAccruedFee;
  onDeselectMatter: () => void;
  maxHeight: string;
}

export const RelevantExternalMatterPage: FC<RelevantExternalMatterPageProps> = ({
  externalMatter,
  accruedFees,
  onDeselectMatter,
  maxHeight,
}) => {
  const { settings } = useSettings();
  const gpmDangerThreshold = settings?.gpmDangerThreshold;
  const gpmWarningThreshold = settings?.gpmWarningThreshold;

  const PA_LABEL = VPDLabel("PracticeArea");

  const totalDuration =
    accruedFees?.accruedTimes.reduce((sum, time) => sum + time.actualDuration, 0) || 1;

  return (
    <>
      <div
        style={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          marginTop: "12px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "12px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            <Button onClick={onDeselectMatter}>Back</Button>
            <h3 style={{ margin: 0 }}>{externalMatter.title}</h3>
          </div>
        </div>
        <div className={matterMetadataClassName}>
          {externalMatter.client && (
            <div className={matterMetadataItemClassName}>
              <span className={matterMetadataLabelClassName}>Client: </span>
              <span className={matterMetadataValueClassName}>{externalMatter.client.name}</span>
            </div>
          )}
          {externalMatter.client && externalMatter.organisationalUnit && (
            <span className={matterMetadataSeparatorClassName}>•</span>
          )}
          {externalMatter.organisationalUnit && (
            <div className={matterMetadataItemClassName}>
              <span className={matterMetadataLabelClassName}>{PA_LABEL}: </span>
              <span className={matterMetadataValueClassName}>
                {externalMatter.organisationalUnit.name}
              </span>
            </div>
          )}
        </div>
        <div className={matterCreationInfoClassName}>
          {externalMatter.externalCreatedAt && (
            <div className={matterMetadataItemClassName}>
              <span className={matterMetadataLabelClassName}>Created on </span>
              <span className={matterMetadataValueClassName}>
                {new Date(externalMatter.externalCreatedAt).toLocaleDateString()}
              </span>
            </div>
          )}
        </div>
        <div style={{ paddingBottom: 0, maxHeight: maxHeight, overflowY: "auto" }}>
          {accruedFees ? (
            <div style={{ marginBottom: "12px" }}>
              <HistoricInfo
                data={{
                  ...accruedFees,
                  gpmDangerThreshold: gpmDangerThreshold,
                  gpmWarningThreshold: gpmWarningThreshold,
                  accruedTimes: accruedFees.accruedTimes.map((time) => ({
                    ...time,
                    feeEarnerName: time.feeEarnerName ?? "",
                    durationAsPercentageOfTotalMatterDuration:
                      (time.actualDuration / totalDuration) * 100,
                  })),
                }}
              />
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
};
